import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import {
  BusinessResponseDto,
  getBusinessList,
  selectActiveBusiness,
} from '~/api/businesses';
import { useAccountStore } from './account';

export const useBusinessesStore = defineStore('businesses', () => {
  const accountsStore = useAccountStore();
  const { currentAccount } = storeToRefs(accountsStore);

  const selectedBusiness = computed<BusinessResponseDto | null>(
    () => currentAccount.value?.currentlySelectedBusiness ?? null,
  );

  const nonNullableSelectedBusiness = computed<BusinessResponseDto>(() => {
    if (selectedBusiness.value === null) {
      throw new Error('Selected business must not be null');
    }
    return selectedBusiness.value;
  });

  const selectBusiness = async (businessId: string | number) => {
    await selectActiveBusiness(businessId);
    await accountsStore.refreshCurrentAccount();
  };

  const businessesCount = ref(-1);

  watch(currentAccount, (newVal, oldVal) => {
    if (newVal === null || newVal.id !== oldVal?.id) {
      businessesCount.value = -1;
    }
  });

  const initialize = async (options: { forceSelectFirst?: boolean } = {}) => {
    if (businessesCount.value >= 0) {
      return;
    }

    const { nodes, totalCount } = await getBusinessList({ perPage: 1 });
    businessesCount.value = totalCount;

    const firstBusiness = nodes.at(0);
    if (!firstBusiness) {
      return;
    }

    const isSelectionStaleOrMissing =
      !selectedBusiness.value ||
      (totalCount === 1 && selectedBusiness.value.id !== firstBusiness.id);

    const shouldAutoSelect =
      isSelectionStaleOrMissing &&
      (totalCount === 1 || options.forceSelectFirst === true);

    if (shouldAutoSelect) {
      await selectBusiness(firstBusiness.id);
    }
  };

  return {
    businessesCount,
    selectedBusiness,
    nonNullableSelectedBusiness,
    initialize,
    selectBusiness,
  };
});
